import { Box, Card, CardContent, Typography } from '@mui/joy';
import { ArrowSquareOut, FileArrowUp, Trash, VideoCamera } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { Image, TextEllipsis } from 'core/elements';
import { formatDate, goTo } from 'core/utils';
import { IPartMediaVideo } from 'parts/types';
import { Color, Level } from 'theme/enums';

import { YOUTUBE_URL_REGEX } from './utils';

interface IProps {
  video: IPartMediaVideo;
  onRemove?: (videoId: number) => Promise<void>;
}

const extractYouTubeId = (url?: string): string | null => {
  if (!url || !YOUTUBE_URL_REGEX.test(url)) {
    return null;
  }
  const patterns = [
    /^https?:\/\/(?:www\.)?youtube\.com\/v\/([^/?&]+)/,
    /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:[\w=&-]*&)*v=([^&]+)/,
    /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([^/?&]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
};

export const VideoTile = ({ video, onRemove }: IProps) => {
  const { t } = useTranslation();

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const youTubeId = extractYouTubeId(video.video);
  const thumbnailUrl = youTubeId ? `https://img.youtube.com/vi/${youTubeId}/hqdefault.jpg` : null;
  const displayName = video.name || t('labelVideo');

  const onWatch = () => {
    if (!video.video || !YOUTUBE_URL_REGEX.test(video.video)) {
      return;
    }
    goTo(video.video, true);
  };

  const handleOnRemove = async () => {
    if (!onRemove) {
      return;
    }
    setShowRemoveConfirmation(false);
    await onRemove(video.id);
  };

  return (
    <>
      <Card sx={{ width: '200px' }}>
        <CardContent sx={{ gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ gap: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  color={Color.PRIMARY}
                  icon={<ArrowSquareOut size={24} />}
                  onClick={onWatch}
                  tooltipProps={{ title: t('labelWatch'), children: null }}
                  sx={{ svg: { height: '24px !important', width: '24px !important' } }}
                />
              </Box>

              {onRemove && (
                <Button
                  color={Color.DANGER}
                  icon={<Trash size={24} />}
                  onClick={() => setShowRemoveConfirmation(true)}
                  sx={{ svg: { height: '24px !important', width: '24px !important' } }}
                />
              )}
            </Box>
            <Box
              onClick={onWatch}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px',
                cursor: 'pointer',
              }}
            >
              {thumbnailUrl ? (
                <Image
                  src={thumbnailUrl}
                  alt={video.name ?? ''}
                  style={{ height: '59px', width: 'auto', maxWidth: '174px', objectFit: 'cover' }}
                />
              ) : (
                <VideoCamera size={48} />
              )}
            </Box>
          </Box>
          <Box sx={{ gap: 0.5 }}>
            <Box>
              <TextEllipsis content={displayName} maxRows={2} sx={{ height: '42px' }} />
            </Box>
            <Box>
              <Typography level={Level.BODY_XS} color={Color.PRIMARY}>
                <FileArrowUp weight="fill" /> {t('labelVideo')}
              </Typography>
              {video.creationDate && (
                <Typography level={Level.BODY_SM}>
                  {formatDate(video.creationDate, true)}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {onRemove && (
        <Confirmation
          isOpen={showRemoveConfirmation}
          title={t('titleRemoveVideo')}
          message={t('messageConfirmRemoveVideo', { name: displayName })}
          confirmBtnLabel={t('buttonLabelRemove')}
          confirmBtnColor={Color.DANGER}
          onConfirm={handleOnRemove}
          onCancel={() => setShowRemoveConfirmation(false)}
        />
      )}
    </>
  );
};
